import React, { useEffect, useCallback, useState } from 'react';
import { TOUR_STEPS } from './config';
import { TourStep } from './GuidedTourTooltip';
import GuidedTourOverlay from './GuidedTourOverlay';
import GuidedTourTooltip from './GuidedTourTooltip';
import { useEditorStore } from '../../store/useEditorStore';

export interface GuidedTourProps {
  visible: boolean;
  onClose: () => void;
  initialStep?: number;
}

const GuidedTour: React.FC<GuidedTourProps> = ({
  visible,
  onClose,
  initialStep = 0,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  
  // 从 store 获取状态和方法
  const { 
    setGuidedTourCompleted,
  } = useEditorStore();

  const currentStepData: TourStep = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  // 计算提示框位置
  const calculateTooltipPosition = useCallback(() => {
    const targetSelector = currentStepData.targetSelector;
    if (!targetSelector) {
      // 没有目标时居中显示
      setTooltipPosition(null);
      return;
    }

    const element = document.querySelector(targetSelector);
    if (!element) {
      setTooltipPosition(null);
      return;
    }

    const rect = element.getBoundingClientRect();
    const position = currentStepData.position || 'bottom';
    
    let x = rect.left;
    let y = rect.top;

    // 根据位置调整
    switch (position) {
      case 'bottom':
        x = rect.left + rect.width / 2;
        y = rect.bottom + 20;
        break;
      case 'top':
        x = rect.left + rect.width / 2;
        y = rect.top - 20;
        break;
      case 'left':
        x = rect.left - 20;
        y = rect.top + rect.height / 2;
        break;
      case 'right':
        x = rect.right + 20;
        y = rect.top + rect.height / 2;
        break;
    }

    // 确保不超出屏幕边界
    x = Math.max(240, Math.min(x, window.innerWidth - 240));
    y = Math.max(200, Math.min(y, window.innerHeight - 400));

    setTooltipPosition({ x, y });
  }, [currentStepData]);

  // 步骤变化时更新位置
  useEffect(() => {
    if (visible) {
      // 延迟计算，确保DOM已渲染
      const timer = setTimeout(calculateTooltipPosition, 100);
      return () => clearTimeout(timer);
    }
  }, [visible, currentStep, calculateTooltipPosition]);

  // 窗口大小变化时重新计算
  useEffect(() => {
    if (!visible) return;
    
    const handleResize = () => {
      calculateTooltipPosition();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [visible, calculateTooltipPosition]);

  // 上一步
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // 下一步
  const handleNext = () => {
    if (isLastStep) {
      // 完成引导
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  // 完成引导
  const handleComplete = () => {
    setGuidedTourCompleted(true);
    onClose();
  };

  // 关闭引导
  const handleClose = () => {
    onClose();
  };

  // 获取高亮目标
  const getHighlightTarget = () => {
    if (!currentStepData.targetSelector) return null;
    return {
      selector: currentStepData.targetSelector,
      padding: 8,
    };
  };

  if (!visible) return null;

  return (
    <>
      {/* 遮罩层和高亮 */}
      <GuidedTourOverlay
        target={getHighlightTarget()}
        visible={visible}
        animationEnabled={true}
      />

      {/* 提示气泡 */}
      <GuidedTourTooltip
        step={currentStepData}
        currentStep={currentStep}
        totalSteps={TOUR_STEPS.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onClose={handleClose}
        position={tooltipPosition || undefined}
        tooltipStyle={{
          // 如果没有位置信息，居中显示
          ...(tooltipPosition ? {} : {
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }),
        }}
      />
    </>
  );
};

export default GuidedTour;

/**
 * 使用示例：
 *
 * 在编辑器页面中引入并使用：
 *
 * ```tsx
 * import GuidedTour from './components/GuidedTour/GuidedTour';
 * import { useEditorStore } from './store/useEditorStore';
 *
 * const EditorPage = () => {
 *   const { isGuidedTourEnabled, setGuidedTourEnabled } = useEditorStore();
 *
 *   return (
 *     <div>
 *       GuidedTour Component
 *       <GuidedTour
 *         visible={isGuidedTourEnabled}
 *         onClose={() => setGuidedTourEnabled(false)}
 *       />
 *     </div>
 *   );
 * };
 * ```
 */
